import { getToken, removeToken } from './auth';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export async function login(email: string, password: string) {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Login failed');
  }
  return res.json();
}

export async function fetchWithAuth(input: RequestInfo, init: RequestInit = {}) {
  const token = getToken();
  const headers = {
    ...(init.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    removeToken();
    window.location.href = '/';
    throw new Error('Unauthorized');
  }
  return res;
}

export async function createLead(data: any) {
  const res = await fetchWithAuth(`${API_URL}/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Create lead failed');
  }
  return res.json();
}

export async function updateLead(id: number, data: any) {
  const res = await fetchWithAuth(`${API_URL}/leads/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update lead failed');
  }
  return res.json();
}

export async function deleteLead(id: number) {
  const res = await fetchWithAuth(`${API_URL}/leads/${id}`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Delete lead failed');
  }
  return res.json();
}

export async function getProjects() {
  const res = await fetchWithAuth(`${API_URL}/projects`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get projects failed');
  }
  return res.json();
}

export async function createProject(data: any) {
  const res = await fetchWithAuth(`${API_URL}/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Create project failed');
  }
  return res.json();
}

export async function updateProject(id: number, data: any) {
  const res = await fetchWithAuth(`${API_URL}/projects/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update project failed');
  }
  return res.json();
}

export async function deleteProject(id: number) {
  const res = await fetchWithAuth(`${API_URL}/projects/${id}`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Delete project failed');
  }
  return res.json();
}

export async function getUsers() {
  const res = await fetchWithAuth(`${API_URL}/users`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get users failed');
  }
  return res.json();
}

export async function createUser(data: any) {
  const res = await fetchWithAuth(`${API_URL}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Create user failed');
  }
  return res.json();
}

export async function updateUser(id: number, data: any) {
  const res = await fetchWithAuth(`${API_URL}/users/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update user failed');
  }
  return res.json();
}

export async function deleteUser(id: number) {
  const res = await fetchWithAuth(`${API_URL}/users/${id}`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Delete user failed');
  }
  return res.json();
}

export async function getAnalyticsLeadsByStatus(dateFrom?: string, dateTo?: string, managerId?: string, projectId?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (managerId) params.append('manager_id', managerId);
  if (projectId) params.append('project_id', projectId);
  const res = await fetchWithAuth(`${API_URL}/analytics/leads-by-status${params.toString() ? `?${params.toString()}` : ''}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get analytics failed');
  }
  return res.json();
}

export async function getAnalyticsLeadsByProject(dateFrom?: string, dateTo?: string, managerId?: string, projectId?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (managerId) params.append('manager_id', managerId);
  if (projectId) params.append('project_id', projectId);
  const res = await fetchWithAuth(`${API_URL}/analytics/leads-by-project${params.toString() ? `?${params.toString()}` : ''}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get analytics failed');
  }
  return res.json();
}

export async function getAnalyticsLeadsByManager(dateFrom?: string, dateTo?: string, managerId?: string, projectId?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (managerId) params.append('manager_id', managerId);
  if (projectId) params.append('project_id', projectId);
  const res = await fetchWithAuth(`${API_URL}/analytics/leads-by-manager${params.toString() ? `?${params.toString()}` : ''}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get analytics failed');
  }
  return res.json();
}

export async function getAnalyticsLeadsDynamics(days: number = 30, dateFrom?: string, dateTo?: string, managerId?: string, projectId?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (managerId) params.append('manager_id', managerId);
  if (projectId) params.append('project_id', projectId);
  if (!dateFrom && !dateTo) params.append('days', String(days));
  const res = await fetchWithAuth(`${API_URL}/analytics/leads-dynamics?${params.toString()}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get analytics failed');
  }
  return res.json();
}

export async function getAnalyticsTotalLeads(dateFrom?: string, dateTo?: string, managerId?: string, projectId?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (managerId) params.append('manager_id', managerId);
  if (projectId) params.append('project_id', projectId);
  const query = params.toString();
  const res = await fetchWithAuth(`${API_URL}/analytics/total-leads${query ? `?${query}` : ''}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get analytics failed');
  }
  return res.json();
}

export async function sendLeadToClient(leadId: number, clientId: number) {
  const res = await fetchWithAuth(`${API_URL}/leads/${leadId}/send-to-client`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_id: clientId })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Send lead to client failed');
  }
  return res.json();
}

export async function bulkSendLeadsToClient(leadIds: number[], clientId: number) {
  const res = await fetchWithAuth(`${API_URL}/leads/bulk-send-to-client`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lead_ids: leadIds, client_id: clientId })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Bulk send leads to client failed');
  }
  return res.json();
}

export async function updateLeadBonus(leadId: number, bonus: boolean) {
  const res = await fetchWithAuth(`${API_URL}/leads/${leadId}/bonus`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bonus })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update lead bonus failed');
  }
  return res.json();
}

// User Projects API
export async function getUserProjects(userId: number) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}/projects`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get user projects failed');
  }
  return res.json();
}

export async function addUserProject(userId: number, projectId: number) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}/projects/${projectId}`, {
    method: 'POST'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Add user project failed');
  }
  return res.json();
}

export async function removeUserProject(userId: number, projectId: number) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}/projects/${projectId}`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Remove user project failed');
  }
  return res.json();
}

export async function updateUserProjects(userId: number, projectIds: number[]) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}/projects`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectIds })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update user projects failed');
  }
  return res.json();
}

// Analytics: Contacts Efficiency
export async function getContactsEfficiency(params: { dateFrom?: string; dateTo?: string; projectId?: string }) {
  const query = new URLSearchParams();
  if (params.dateFrom) query.append('date_from', params.dateFrom);
  if (params.dateTo) query.append('date_to', params.dateTo);
  if (params.projectId) query.append('project_id', params.projectId);
  const url = `${API_URL}/analytics/contacts-efficiency${query.toString() ? `?${query.toString()}` : ''}`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get contacts efficiency failed');
  }
  return res.json();
}

export async function getAnalyticsLeadsByClientStatus(dateFrom?: string, dateTo?: string, managerId?: string, projectId?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (managerId) params.append('manager_id', managerId);
  if (projectId) params.append('project_id', projectId);
  const res = await fetchWithAuth(`${API_URL}/analytics/leads-by-client-status${params.toString() ? `?${params.toString()}` : ''}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get analytics failed');
  }
  return res.json();
}

export async function getAnalyticsLnbStats(dateFrom?: string, dateTo?: string, managerId?: string, projectId?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (managerId) params.append('manager_id', managerId);
  if (projectId) params.append('project_id', projectId);
  const res = await fetchWithAuth(`${API_URL}/analytics/lnb-stats${params.toString() ? `?${params.toString()}` : ''}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get LNB stats failed');
  }
  return res.json();
}

export async function getAnalyticsHotStats(dateFrom?: string, dateTo?: string, managerId?: string, projectId?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (managerId) params.append('manager_id', managerId);
  if (projectId) params.append('project_id', projectId);
  const res = await fetchWithAuth(`${API_URL}/analytics/hot-stats${params.toString() ? `?${params.toString()}` : ''}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get Hot stats failed');
  }
  return res.json();
}

export async function getAnalyticsApprovedSentLeads(dateFrom?: string, dateTo?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  const res = await fetchWithAuth(`${API_URL}/analytics/approved-sent-leads${params.toString() ? `?${params.toString()}` : ''}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get approved sent leads failed');
  }
  return res.json();
}

export async function fetchClientStatuses(): Promise<string[]> {
  const res = await fetchWithAuth(`${API_URL}/leads/client-statuses`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Fetch client statuses failed');
  }
  return res.json();
}

export async function fetchLeads(filters: {
  page?: number;
  limit?: number;
  status?: string;
  phone?: string;
  projectId?: string;
  clientStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  sentDateFrom?: string;
  sentDateTo?: string;
  activeTab?: number;
}) {
  const params = new URLSearchParams();

  // Добавляем параметры пагинации
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  // Общие фильтры для всех вкладок
  if (filters.projectId) params.append('project_id', filters.projectId);
  if (filters.clientStatus) params.append('client_status', filters.clientStatus);
  if (filters.dateFrom) params.append('date_from', filters.dateFrom);
  if (filters.dateTo) params.append('date_to', filters.dateTo);
  if (filters.sentDateFrom) params.append('sent_date_from', filters.sentDateFrom);
  if (filters.sentDateTo) params.append('sent_date_to', filters.sentDateTo);
  
  // Фильтры в зависимости от активной вкладки
  if (filters.activeTab === 0) {
    // Вкладка "Все" - поиск по телефону и статусу
    if (filters.status) params.append('status', filters.status);
    if (filters.phone) params.append('phone', filters.phone);
    // По умолчанию показываем только валидные и неотправленные лиды
    params.append('invalid_lead', 'false');
    params.append('is_sent', 'false');
  } else if (filters.activeTab === 1) {
    // Вкладка "Горячие" - call_result_name = Горячий лид
    if (filters.status) {
      params.append('status', filters.status);
    } else {
      params.append('status', 'UNPROCESSED,BONUS');
    }
    if (filters.phone) params.append('phone', filters.phone);
    params.append('call_result_name', 'Горячий лид');
    // Показываем только валидные и неотправленные лиды
    params.append('invalid_lead', 'false');
    params.append('is_sent', 'false');
  } else if (filters.activeTab === 2) {
    // Вкладка "ЛНБ" - call_result_name = ЛНБ
    if (filters.status) {
      params.append('status', filters.status);
    } else {
      params.append('status', 'UNPROCESSED,BONUS');
    }
    if (filters.phone) params.append('phone', filters.phone);
    params.append('call_result_name', 'ЛНБ');
    // Показываем только валидные и неотправленные лиды
    params.append('invalid_lead', 'false');
    params.append('is_sent', 'false');
  } else if (filters.activeTab === 3) {
    // Вкладка "Отправленные" - is_sent = true
    if (filters.status) params.append('status', filters.status);
    if (filters.phone) params.append('phone', filters.phone);
    params.append('is_sent', 'true');
    // Показываем только валидные лиды
    params.append('invalid_lead', 'false');
  } else if (filters.activeTab === 4) {
    // Вкладка "Брак" - invalid_lead = true
    if (filters.status) params.append('status', filters.status);
    if (filters.phone) params.append('phone', filters.phone);
    params.append('invalid_lead', 'true');
  }
  
  const res = await fetchWithAuth(`${API_URL}/leads?${params.toString()}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Fetch leads failed');
  }
  return res.json();
}

// Disputes API (admin)
export async function getDisputHistory(leadId: number) {
  const res = await fetchWithAuth(`${API_URL}/disputs/${leadId}/history`);
  if (!res.ok) throw new Error('Fetch disput history failed');
  return res.json();
}

export async function getDisputMessages(leadId: number) {
  const res = await fetchWithAuth(`${API_URL}/disputs/${leadId}/messages`);
  if (!res.ok) throw new Error('Fetch disput messages failed');
  return res.json();
}

export async function postDisputMessage(leadId: number, message: string) {
  const res = await fetchWithAuth(`${API_URL}/disputs/${leadId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message })
  });
  if (!res.ok) throw new Error('Post disput message failed');
  return res.json();
}

export async function setDisputStatus(leadId: number, status: 'На проверке' | 'Утверждено' | 'Не утверждено') {
  const res = await fetchWithAuth(`${API_URL}/disputs/${leadId}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status })
  });
  if (!res.ok) throw new Error('Update disput status failed');
  return res.json();
}

// Integrations API
export async function getIntegrations() {
  const res = await fetchWithAuth(`${API_URL}/integrations`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get integrations failed');
  }
  return res.json();
}

export async function createIntegration(data: any) {
  const res = await fetchWithAuth(`${API_URL}/integrations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Create integration failed');
  }
  return res.json();
}

export async function updateIntegration(id: number, data: any) {
  const res = await fetchWithAuth(`${API_URL}/integrations/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update integration failed');
  }
  return res.json();
}

export async function deleteIntegration(id: number) {
  const res = await fetchWithAuth(`${API_URL}/integrations/${id}`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Delete integration failed');
  }
  return res.json();
} 

export async function getClients() {
  const res = await fetchWithAuth(`${API_URL}/clients`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get clients failed');
  }
  return res.json();
}

// Finance API
interface FinanceQueryParams {
  projectId?: number | string;
  clientId?: number | string;
  status?: string;
  limit?: number;
  offset?: number;
  from?: string;
  to?: string;
  groupBy?: 'day' | 'week' | 'month';
}

export async function getFinancePackages(params: FinanceQueryParams = {}) {
  const query = new URLSearchParams();
  if (params.projectId) query.append('projectId', String(params.projectId));
  if (params.clientId) query.append('clientId', String(params.clientId));
  if (params.status) query.append('status', params.status);
  if (params.limit) query.append('limit', String(params.limit));
  if (params.offset) query.append('offset', String(params.offset));
  const url = `${API_URL}/finance/packages${query.toString() ? `?${query.toString()}` : ''}`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get finance packages failed');
  }
  return res.json();
}

export async function createFinancePackage(payload: {
  projectId: number;
  secondaryProjectIds?: number[];
  clientId?: number | null;
  title?: string;
  leadQuota: number;
  unitPrice?: number | null;
  totalAmount: number;
  paidAmount?: number | null;
  paymentStatus: string;
  paymentDate?: string | null;
  invoiceNumber?: string | null;
  notes?: string | null;
}) {
  const res = await fetchWithAuth(`${API_URL}/finance/packages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Create finance package failed');
  }
  return res.json();
}

export async function updateFinancePackage(
  id: number,
  payload: Partial<{
    projectId: number;
    secondaryProjectIds: number[];
    clientId: number | null;
    title: string | null;
    leadQuota: number;
    unitPrice: number | null;
    totalAmount: number;
    paidAmount: number | null;
    paymentStatus: string;
    paymentDate: string | null;
    invoiceNumber: string | null;
    notes: string | null;
  }>,
) {
  const res = await fetchWithAuth(`${API_URL}/finance/packages/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update finance package failed');
  }
  return res.json();
}

export interface StopSource {
  id: number;
  client_user_id: number;
  pattern_type: 'contact';
  pattern: string;
  note: string | null;
  created_at: string;
}

export async function getClientStopSources(clientId: number): Promise<StopSource[]> {
  const res = await fetchWithAuth(`${API_URL}/clients/${clientId}/stop-sources`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get stop sources failed');
  }
  return res.json();
}

export async function addClientStopSource(clientId: number, data: { pattern: string; note?: string }): Promise<StopSource> {
  const res = await fetchWithAuth(`${API_URL}/clients/${clientId}/stop-sources`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Add stop source failed');
  }
  return res.json();
}

export async function deleteClientStopSource(id: number): Promise<void> {
  const res = await fetchWithAuth(`${API_URL}/clients/stop-sources/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Delete stop source failed');
  }
}

export async function getProjectsDynamicSummary(): Promise<Array<{
  id: number;
  external_project_id: number | null;
  name: string;
  is_active: boolean;
  total_quota: number;
  total_paid: number;
  avg_unit_price: number;
  active_package_id: number | null;
  paid_balance: number;
  leads_sent: number;
  leads_approved: number;
  leads_total: number;
}>> {
  const res = await fetchWithAuth(`${API_URL}/projects/dynamic-summary`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get dynamic summary failed');
  }
  return res.json();
}

export async function deleteFinancePackage(id: number) {
  const res = await fetchWithAuth(`${API_URL}/finance/packages/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Delete finance package failed');
  }
  return res.json();
}

export async function getFinanceEntries(params: FinanceQueryParams = {}) {
  const query = new URLSearchParams();
  if (params.projectId) query.append('projectId', String(params.projectId));
  if (params.clientId) query.append('clientId', String(params.clientId));
  if (params.limit) query.append('limit', String(params.limit));
  if (params.offset) query.append('offset', String(params.offset));
  const url = `${API_URL}/finance/entries${query.toString() ? `?${query.toString()}` : ''}`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get finance entries failed');
  }
  return res.json();
}

export async function createFinanceAdjustment(payload: {
  projectId: number;
  clientId?: number | null;
  amount: number;
  leadCount?: number;
  description?: string;
  effectiveAt?: string;
}) {
  const res = await fetchWithAuth(`${API_URL}/finance/adjustments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Create finance adjustment failed');
  }
  return res.json();
}

export async function getFinanceSummary(params: FinanceQueryParams = {}) {
  const query = new URLSearchParams();
  if (params.projectId) query.append('projectId', String(params.projectId));
  if (params.from) query.append('from', params.from);
  if (params.to) query.append('to', params.to);
  const url = `${API_URL}/finance/summary${query.toString() ? `?${query.toString()}` : ''}`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get finance summary failed');
  }
  return res.json();
}

export async function getFinanceTimeseries(params: FinanceQueryParams = {}) {
  const query = new URLSearchParams();
  if (params.projectId) query.append('projectId', String(params.projectId));
  if (params.from) query.append('from', params.from);
  if (params.to) query.append('to', params.to);
  if (params.groupBy) query.append('groupBy', params.groupBy);
  const url = `${API_URL}/finance/timeseries${query.toString() ? `?${query.toString()}` : ''}`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get finance timeseries failed');
  }
  return res.json();
}

// Google Sheets Export API
export async function getGoogleSheetsStatus() {
  const res = await fetchWithAuth(`${API_URL}/google-sheets/status`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get Google Sheets status failed');
  }
  return res.json();
}

export async function getGoogleSheetsSettings(projectId: number) {
  const res = await fetchWithAuth(`${API_URL}/google-sheets/settings/${projectId}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get Google Sheets settings failed');
  }
  return res.json();
}

export async function linkGoogleSheet(projectId: number, spreadsheetId: string) {
  const res = await fetchWithAuth(`${API_URL}/google-sheets/link/${projectId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ spreadsheetId })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Link Google Sheet failed');
  }
  return res.json();
}

export async function exportProjectToGoogleSheets(projectId: number, exportAll: boolean = false) {
  const url = exportAll 
    ? `${API_URL}/google-sheets/export/project/${projectId}?exportAll=true`
    : `${API_URL}/google-sheets/export/project/${projectId}`;
  const res = await fetchWithAuth(url, {
    method: 'POST'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Export to Google Sheets failed');
  }
  return res.json();
}

export async function exportAllToGoogleSheets() {
  const res = await fetchWithAuth(`${API_URL}/google-sheets/export/all`, {
    method: 'POST'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Export all to Google Sheets failed');
  }
  return res.json();
}

export async function getGoogleSheetsHistory(projectId?: number, limit: number = 50) {
  const params = new URLSearchParams();
  if (projectId) params.append('projectId', String(projectId));
  params.append('limit', String(limit));
  const url = `${API_URL}/google-sheets/history?${params.toString()}`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get Google Sheets history failed');
  }
  return res.json();
}// Unit Economics API
export async function getUnitEconomicsSummary(projectId: number, month: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/project/${projectId}/summary?month=${month}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get unit economics summary failed');
  }
  return res.json();
}

export async function recalculateUnitEconomics(projectId: number, month: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/recalculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, month })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Recalculate unit economics failed');
  }
  return res.json();
}

export async function getUnitEconomicsExpenses(projectId?: number, month?: string) {
  const params = new URLSearchParams();
  if (projectId) params.append('projectId', String(projectId));
  if (month) params.append('month', month);
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/expenses?${params.toString()}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get unit economics expenses failed');
  }
  return res.json();
}

export async function addUnitEconomicsExpense(data: any) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/expenses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Add unit economics expense failed');
  }
  return res.json();
}

export async function getUnitEconomicsCategories() {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/categories`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get unit economics categories failed');
  }
  return res.json();
}

// Operator Activity API
export async function populateOperatorActivity(from: string, to: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/populate-activity?from=${from}&to=${to}`, {
    method: 'POST'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Populate operator activity failed');
  }
  return res.json();
}

export async function getOperatorActivity(operatorName?: string, month?: string) {
  const params = new URLSearchParams();
  if (operatorName) params.append('operator', operatorName);
  if (month) params.append('month', month);
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/operator-activity?${params.toString()}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get operator activity failed');
  }
  return res.json();
}

export async function updateOperatorActivity(id: number, data: { hours_manual_override?: number | null }) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/operator-activity/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update operator activity failed');
  }
  return res.json();
}

export async function getOperatorNames() {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/operator-names`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get operator names failed');
  }
  return res.json();
}

// Operator Reports API
export async function getOperatorsReport(month: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/operators/report?month=${month}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get operators report failed');
  }
  return res.json();
}

export async function getOperatorReport(name: string, month: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/operators/${encodeURIComponent(name)}/report?month=${month}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get operator report failed');
  }
  return res.json();
}

export async function getOperatorTrends(name: string, months: number = 6) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/operators/${encodeURIComponent(name)}/trends?months=${months}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get operator trends failed');
  }
  return res.json();
}

// Operator-Project Matrix API
export async function getOperatorProjectMatrix(month: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/operator-project-matrix?month=${month}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get operator-project matrix failed');
  }
  return res.json();
}

export function getOperatorProjectMatrixExportUrl(month: string): string {
  return `${API_URL}/finance/unit-economics/operator-project-matrix/export?month=${month}`;
}

// Employee Roles API
export async function getEmployeeRoles() {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/employee-roles`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get employee roles failed');
  }
  return res.json();
}

export async function createEmployeeRole(data: { userId: number; roleType: string; baseSalary?: number }) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/employee-roles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Create employee role failed');
  }
  return res.json();
}

export async function updateEmployeeRole(id: number, data: { roleType?: string; baseSalary?: number }) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/employee-roles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update employee role failed');
  }
  return res.json();
}

export async function deleteEmployeeRole(id: number) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/employee-roles/${id}`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Delete employee role failed');
  }
  return res.json();
}

// ROP Team API
export async function getRopTeam(userId: number) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/rop/${userId}/team`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get ROP team failed');
  }
  return res.json();
}

export async function updateRopTeam(userId: number, operatorUserIds: number[]) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/rop/${userId}/team`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operatorUserIds })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update ROP team failed');
  }
  return res.json();
}

export async function getAvailableOperators(ropUserId?: number) {
  const params = new URLSearchParams();
  if (ropUserId) params.append('ropUserId', String(ropUserId));
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/available-operators?${params.toString()}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get available operators failed');
  }
  return res.json();
}

// Commission Settings API
export async function getCommissionSettings() {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/commission-settings`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get commission settings failed');
  }
  return res.json();
}

export async function updateCommissionSetting(id: number, data: { percentage?: number | null; fixedAmount?: number | null; threshold?: number | null }) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/commission-settings/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Update commission setting failed');
  }
  return res.json();
}

export async function getCommissionRates() {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/commission-rates`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get commission rates failed');
  }
  return res.json();
}

// ROP/MOP Salary API
export async function getRopSalary(userId: number, month: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/rop/${userId}/salary?month=${month}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get ROP salary failed');
  }
  return res.json();
}

export async function getMopSalary(userId: number, month: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/mop/${userId}/salary?month=${month}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get MOP salary failed');
  }
  return res.json();
}

export async function getSalaryReport(month: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/salary-report?month=${month}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get salary report failed');
  }
  return res.json();
}

export async function getGlobalExpenses(month: string) {
  const res = await fetchWithAuth(`${API_URL}/finance/unit-economics/global-expenses?month=${month}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Get global expenses failed');
  }
  return res.json();
}

// Marketplace API
export async function getMarketplaceRegions(): Promise<{ regions: { id: string; name: string; sortOrder: number }[] }> {
  const res = await fetchWithAuth(`${API_URL}/leads/marketplace-regions`);
  if (!res.ok) {
    throw new Error('Failed to fetch marketplace regions');
  }
  return res.json();
}

export async function getMarketplaceCities(regionName: string): Promise<{ cities: { id: string; name: string }[] }> {
  const res = await fetchWithAuth(`${API_URL}/leads/marketplace-cities?regionName=${encodeURIComponent(regionName)}`);
  if (!res.ok) {
    throw new Error('Failed to fetch marketplace cities');
  }
  return res.json();
}

export async function publishLeadsToMarketplace(
  leadIds: number[],
  options: { region?: string; city?: string; niche?: string; price?: number }
) {
  const res = await fetchWithAuth(`${API_URL}/leads/publish-marketplace`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ leadIds, ...options })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Publish to marketplace failed');
  }
  return res.json();
}

